import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

export const ResetPassword: React.FC = () => {
    const navigate = useNavigate();

    const [password, setPassword]                     = useState('');
    const [confirmPassword, setConfirmPassword]       = useState('');
    const [showPassword, setShowPassword]             = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading]   = useState(false);
    const [checking, setChecking] = useState(true);
    const [sessionReady, setSessionReady] = useState(false);

    const handledRef = useRef(false);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;

        const markReady = () => {
            if (handledRef.current) return;
            handledRef.current = true;
            clearTimeout(timeoutId);
            window.history.replaceState(null, '', '/reset-password');
            setSessionReady(true);
            setChecking(false);
        };

        const markInvalid = () => {
            if (handledRef.current) return;
            handledRef.current = true;
            clearTimeout(timeoutId);
            toast.error('Ссылка устарела или недействительна. Запросите новую.');
            navigate('/login', { replace: true });
        };

        // Слушаем auth события через supabase (у него detectSessionInUrl: true)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('[ResetPassword] auth event:', event, !!session);
            if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session) {
                markReady();
            }
        });

        const checkSession = async () => {
            try {
                // Проверяем сессию через supabase
                const { data: { session } } = await supabase.auth.getSession();
                console.log('[ResetPassword] getSession:', !!session);

                if (session) {
                    markReady();
                    return;
                }

                // Fallback: ручной разбор хэша URL
                const hash = window.location.hash.substring(1);
                if (hash) {
                    const params      = new URLSearchParams(hash);
                    const accessToken = params.get('access_token');
                    const refreshToken = params.get('refresh_token');
                    const hashError   = params.get('error');

                    if (hashError) { markInvalid(); return; }

                    if (accessToken && refreshToken) {
                        const { error: setErr } = await supabase.auth.setSession({
                            access_token:  accessToken,
                            refresh_token: refreshToken,
                        });
                        if (setErr) { markInvalid(); return; }
                        // markReady() сработает через onAuthStateChange SIGNED_IN
                        return;
                    }
                }

                // Ждём 5 секунд — Supabase может ещё не успел обработать токен из URL
                timeoutId = setTimeout(() => {
                    if (!handledRef.current) markInvalid();
                }, 5000);

            } catch (err) {
                console.error('[ResetPassword] checkSession error:', err);
                markInvalid();
            }
        };

        checkSession();

        return () => {
            subscription.unsubscribe();
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password.length < 6) {
            toast.error('Пароль должен содержать минимум 6 символов');
            return;
        }
        if (password !== confirmPassword) {
            toast.error('Пароли не совпадают');
            return;
        }

        setLoading(true);
        const toastId = toast.loading('Обновление пароля...');

        try {
            // Обновляем пароль через supabase
            const { error: updateError } = await supabase.auth.updateUser({ password });

            if (updateError) {
                const isSamePassword =
                    updateError.message.toLowerCase().includes('same as the old password') ||
                    updateError.message.toLowerCase().includes('different from the old password') ||
                    updateError.message.toLowerCase().includes('should be different');

                toast.error(
                    isSamePassword
                        ? 'Новый пароль должен отличаться от старого'
                        : updateError.message,
                    { id: toastId }
                );
                setLoading(false);
                return;
            }

            toast.success('Пароль успешно изменён!', { id: toastId });

            try {
                await supabase.auth.signOut({ scope: 'local' });
            } catch (err) {
                console.warn('[ResetPassword] signOut warning (ignored):', err);
            }

            window.location.replace('/login?reset=success');

        } catch (err: any) {
            console.error('[ResetPassword] submit error:', err);
            toast.error(`Ошибка: ${err?.message || 'Неизвестная ошибка'}`, { id: toastId });
            setLoading(false);
        }
    };

    if (checking) {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Проверка ссылки...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!sessionReady) {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div className="error-icon">❌</div>
                        <p>Сессия не найдена. Перейдите по ссылке из письма ещё раз.</p>
                        <button
                            onClick={() => navigate('/login')}
                            className="auth-button"
                            style={{ marginTop: '20px' }}
                        >
                            На страницу входа
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h1 className="auth-title">Ruyo Market</h1>
                <p className="auth-subtitle">Создание нового пароля</p>

                <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
                    <div className="form-group">
                        <label className="auth-label">Новый пароль</label>
                        <div className="password-input-group">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Введите новый пароль"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="auth-input"
                                autoComplete="new-password"
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(v => !v)}
                                disabled={loading}
                            >
                                {showPassword
                                    ? <EyeSlashIcon style={{ width: 20, height: 20 }} />
                                    : <EyeIcon style={{ width: 20, height: 20 }} />}
                            </button>
                        </div>
                        <p className="field-note">Минимум 6 символов</p>
                    </div>

                    <div className="form-group">
                        <label className="auth-label">Подтверждение пароля</label>
                        <div className="password-input-group">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Подтвердите новый пароль"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="auth-input"
                                autoComplete="new-password"
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(v => !v)}
                                disabled={loading}
                            >
                                {showConfirmPassword
                                    ? <EyeSlashIcon style={{ width: 20, height: 20 }} />
                                    : <EyeIcon style={{ width: 20, height: 20 }} />}
                            </button>
                        </div>
                    </div>

                    <button type="submit" disabled={loading} className="auth-button">
                        {loading ? 'Сохранение...' : 'Установить новый пароль'}
                    </button>
                </form>
            </div>
        </div>
    );
};